// AI Generated File
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { DateTimeService } from 'app/services/date-time/date-time.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { combineLatest } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'page-details-section-statistics-summary',
    standalone: false,
    template: `
        <div class="ml-3 mr-1 card panel-monsido-card">
            <div class="card-body pt-0">
                <div class="row align-items-stretch statistics-summary-general visitor-summary border-bottom">
                    <div class="col-9"
                         tabindex="0"
                         [ngClass]="{'active': currentType === 'visits'}"
                         [attr.aria-label]="'Total Visits' | translate"
                         (click)="setType('visits')">
                        <statistics-information [title]="('Total Visits' | translate) + ':'"
                                                [value]="(summary?.visits || 0) | monNumber">
                        </statistics-information>
                    </div>
                    <div class="col-9"
                         tabindex="0"
                         [ngClass]="{'active': currentType === 'pageviews'}"
                         [attr.aria-label]="'Page Views' | translate"
                         (click)="setType('pageviews')">
                        <statistics-information [title]="('Page Views' | translate) + ':'"
                                                [value]="(summary?.pageviews || 0) | monNumber">
                        </statistics-information>
                    </div>
                    <div class="col-9"
                         tabindex="0"
                         [ngClass]="{'active': currentType === 'avg_time'}"
                         [attr.aria-label]="'Average visit time' | translate"
                         (click)="setType('avg_time')">
                        <statistics-information [title]="('Average visit time' | translate) + ':'"
                                                [value]="(summary?.avg_time || 0) | monNumber:'00:00:00'">
                        </statistics-information>
                    </div>
                    <div class="col-9"
                         tabindex="0"
                         [ngClass]="{'active': currentType === 'bounce_rate'}"
                         [attr.aria-label]="'Bounce rate' | translate"
                         (click)="setType('bounce_rate')">
                        <statistics-information [title]="('Bounce rate' | translate) + ':'"
                                                [value]="getBounceRateValue()">
                        </statistics-information>
                    </div>
                </div>
                <mon-spinner [loading]="loading" class="block mt-9">
                    <mon-chart-line [height]="300"
                                [data]="lineChartData"
                                [labels]="lineChartLabels"
                                [series]="lineChartSeries"
                                [colors]="lineChartColors"
                                [options]="lineChartOptions"
                                [override]="lineChartOverride">
                    </mon-chart-line>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class PageDetailsSectionStatisticsSummaryComponent implements OnInit, OnChanges {
    @Input() page: any;
    @Input() startDate: any;
    @Input() endDate: any;

    currentType = 'visits';
    summary: any;
    loading = false;
    visitorData: any[] = [];

    lineChartSeries: string[] = [];
    lineChartData: any[][] = [[]];
    lineChartColors: string[] = [];
    lineChartLabels: string[] = [];
    lineChartOverride: any[] = [];
    lineChartOptions: any = {};

    constructor (
        private pageService: PageService,
        private translateService: TranslateService,
        private dateTimeService: DateTimeService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.currentType = 'visits';
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (changes.startDate || changes.endDate) {
            const params = {
                from: this.startDate.format('YYYY-MM-DD'),
                to: this.endDate.format('YYYY-MM-DD'),
            };
            this.setupLineChart();
            this.loading = true;

            combineLatest([
                this.getStatisticsSummary(params),
                this.renewData(params),
            ]).pipe(
                finalize(() => {
                    this.loading = false;
                }),
            ).subscribe();
        }
    }

    setType (type: string): void {
        this.currentType = type;
        this.setupLineChartData();
    }

    setupLineChart (): void {
        this.lineChartSeries = [this.translateService.getString('Visits')];
        this.lineChartData = [[]];
        this.lineChartColors = ['#50a6fb'];
        this.lineChartLabels = [];
        this.lineChartOverride = [
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderColor: '#50a6fb',
            },
        ];

        this.lineChartOptions = {
            tooltips: {
                mode: 'label',
                intersect: false,
                callbacks: {
                    title: (tooltipItem: any, data: any) => {
                        return data.labels[tooltipItem[0].index];
                    },
                    label: (tooltipItem: any, data: any) => {
                        if (this.currentType === 'avg_time') {
                            return (
                                this.getChartLabel() +
                                this.pipeService.get('monNumber')(
                                    data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index],
                                    '00:00:00',
                                )
                            );
                        } else if (this.currentType === 'bounce_rate') {
                            return (
                                this.getChartLabel() +
                                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] +
                                '%'
                            );
                        }
                        return (
                            this.getChartLabel() +
                            this.pipeService.get('monNumber')(
                                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index],
                                '0,0',
                            )
                        );
                    },
                },
            },
            scales: {
                yAxes: [
                    {
                        id: 'y-axis-0',
                        type: 'linear',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            callback: (value: any, index: any) => {
                                if (this.currentType === 'avg_time') {
                                    return this.pipeService.get('monNumber')(value, '00:00:00');
                                } else if (this.currentType === 'bounce_rate') {
                                    return parseInt(value) + '%';
                                }
                                return this.pipeService.get('monNumber')(value, '0,0');
                            },
                            min: 0,
                        },
                    },
                ],
                xAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                        ticks: {
                            callback: (value: any) => {
                                if (value) {
                                    return this.dateTimeService.format(value, 'MMM DD', 'll');
                                }
                                return '';
                            },
                        },
                    },
                ],
            },
            legend: {
                display: false,
            },
        };
    }

    renewData (params: any): any {
        return this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'total' })).then((data: any) => {
            this.visitorData = data;
            this.setupLineChartData();
        }, () => {});
    }

    setupLineChartData (): void {
        this.lineChartData = [[]];
        this.lineChartLabels = [];
        for (let k = 0; k < this.visitorData.length; k++) {
            this.lineChartLabels.push(this.pipeService.get('userDate')(this.visitorData[k].date, 'll'));
            this.lineChartData[0].push(this.getVisits(this.visitorData[k]));
        }
    }

    getStatisticsSummary (params: any): any {
        return this.pageService
            .getStatSummary(this.page.id, safeClone({ ...params, type: 'referring' }))
            .then((summary: any) => {
                this.summary = summary;
            });
    }

    getVisits (statistic: any): number {
        if (typeof statistic === 'undefined') {
            return 0;
        }
        if (this.currentType === 'bounce_rate') {
            return Number(statistic.bounce_rate.replace('%', ''));
        } else if (this.currentType === 'avg_time') {
            return statistic.avg_time;
        }
        return Number(statistic[this.currentType] || 0);
    }

    getChartLabel (): string {
        let txt = this.translateService.getString('Visits');
        switch (this.currentType) {
            case 'visits':
                txt = this.translateService.getString('Visits');
                break;
            case 'uniq_visitors':
                txt = this.translateService.getString('Total Visitors');
                break;
            case 'pageviews':
                txt = this.translateService.getString('Page Views');
                break;
            case 'avg_time':
                txt = this.translateService.getString('Avg. Visit Time');
                break;
            case 'bounce_rate':
                txt = this.translateService.getString('Bounce rates');
                break;
        }

        return ' ' + txt + ': ';
    }

    getBounceRateValue (): string {
        const bounceRate = this.summary?.bounce_rate || 'N/A';
        if (bounceRate === 'N/A') {
            return bounceRate;
        }
        return bounceRate + (bounceRate.indexOf('%') > -1 ? '' : '%');
    }
}
